import { useEffect, useMemo, useState } from "react";
import { Paper } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { useUserContext } from "../../contexts";
import { apiAccessRequest } from "../../constants";
import { BuildUrl } from "../../helpers";
import { PatientSearchResponse } from "../../models";
import { SearchInput } from "../Common";

interface AuditLogPatientSearchProps {
	label?: string;
	value?: string;
	onPatientSelect?: (patient?: PatientSearchResponse) => void;
}

export const AuditLogPatientSearch = ({
	label = "Patient",
	value,
	onPatientSelect
}: AuditLogPatientSearchProps) => {
	const { instance } = useMsal();
	const { account } = useUserContext();
	const [searchValue, setSearchValue] = useState("");
	const [filteredPatients, setFilteredPatients] = useState<
		PatientSearchResponse[]
	>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setSearchValue(value ?? "");
	}, [value]);

	const handleSearchValueChange = (nextValue: string) => {
		setSearchValue(nextValue);

		if (nextValue.trim() === "" && value) {
			setFilteredPatients([]);
			onPatientSelect?.(undefined);
		}
	};

	useEffect(() => {
		const searchTerm = searchValue.trim();
		const selectedValue = value?.trim().toLowerCase() ?? "";

		if (searchTerm.length < 1 || searchTerm.toLowerCase() === selectedValue) {
			setFilteredPatients([]);
			setIsLoading(false);
			return;
		}

		const fetchPatients = async () => {
			setIsLoading(true);
			try {
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});

				const response = await fetch(BuildUrl("Auditlog/patientsearch"), {
					method: "POST",
					headers: {
						Authorization: `Bearer ${tokenResponse.accessToken}`,
						"Content-Type": "application/json"
					},
					body: JSON.stringify(
						searchTerm
							.split(/\s+/)
							.map((part) => part.trim())
							.filter((part) => part.length > 0)
					)
				});

				if (!response.ok) {
					throw new Error("Network response was not ok");
				}

				const contentLength = response.headers.get("Content-Length");
				if (response.status === 204 || contentLength === "0") {
					setFilteredPatients([]);
					return;
				}

				const result = (await response.json()) as PatientSearchResponse[];
				setFilteredPatients(result);
			} catch (error) {
				console.log("AuditLogPatientSearch fetchPatients", error);
				setFilteredPatients([]);
			} finally {
				setIsLoading(false);
			}
		};

		void fetchPatients();
	}, [account, instance, searchValue, value]);

	const handlePatientClick = (patient?: PatientSearchResponse) => {
		setSearchValue(patient?.patientName ?? "");
		setFilteredPatients([]);
		onPatientSelect?.(patient);
	};

	const shouldShowDropdown = useMemo(() => {
		const selectedValue = value?.trim().toLowerCase() ?? "";
		return (
			searchValue.trim().length > 0 &&
			searchValue.trim().toLowerCase() !== selectedValue
		);
	}, [searchValue, value]);

	return (
		<div style={{ position: "relative", width: "100%" }}>
			<SearchInput
				label={label}
				value={searchValue}
				handleSearchValueChange={handleSearchValueChange}
				handleClear={() => handlePatientClick(undefined)}
			/>
			{shouldShowDropdown && (
				<Paper
					elevation={3}
					style={{
						position: "absolute",
						top: "100%",
						left: 0,
						right: 0,
						zIndex: 10,
						maxHeight: 400,
						overflowY: "auto",
						marginTop: 8
					}}
				>
					{isLoading ? (
						<div style={{ padding: "12px 16px" }}>Loading...</div>
					) : filteredPatients.length > 0 ? (
						filteredPatients.map((patient) => (
							<button
								key={patient.patientId}
								type="button"
								onClick={() => handlePatientClick(patient)}
								style={{
									display: "block",
									width: "100%",
									padding: "12px 16px",
									textAlign: "left",
									border: 0,
									background: "transparent",
									cursor: "pointer"
								}}
							>
								{patient.patientName}
							</button>
						))
					) : (
						<div style={{ padding: "12px 16px" }}>No patients found</div>
					)}
				</Paper>
			)}
		</div>
	);
};
